/**
 * What each role is called on this page, and what it may do on it.
 *
 * The words are `mockups/client/settings.html`'s own People table — `Owner · Yes`,
 * `Billing · Invoices`, `Member · Comment` — and the predicates are
 * `ARCHITECTURE.md` §Identities and roles read from the client's side.
 *
 * ## The predicates are derived, not restated
 *
 * `ROLE_PERMISSIONS` is the matrix the **services** check — `assertRoleCan`, and Better
 * Auth's own access-control roles through `modules/auth/access.ts` — so a button this
 * file showed that that table refused was a 403 the reader could not have predicted.
 * It used to live in `apps/api/src/security/permissions.ts`, which `apps/web` may not
 * import, so this file held a hand-written copy that lint could not bind.
 *
 * Session 19 moved the table to `packages/shared/src/schemas/clients.ts`, beside
 * `CLIENT_ROLES`; the API re-exports it and nothing there changed. What is left here is
 * page copy and the two rules that are not permissions:
 *
 * | Here | The rule |
 * |---|---|
 * | `managesSeats`, `editsInvoiceDetails` | `members:manage` — `clients.service.assertAccountAdmin`, and `update` asserts the same |
 * | `choosesRail` | `invoices:pay` — `billing.charge.assertMayPay` |
 * | `approvesMoney` | `money:approve` — `usage.service.assertMayManageWidget` |
 * | `appointsOwners` | `clients.service.assertMayChangeOwnership` — **not** a `ClientPermission`: it is a rule about the role being *granted*, so `members:manage` does not answer it |
 * | `raisesRequests` | `requests:write` — `work_orders.service.assertMayRequest`, and `files.service.recordReview` asserts the same |
 * | `seesPeople` | not a service rule — `.docs/19` §Edge cases, "billing … sees billing, settings (payment), invoices" |
 * | `writesSite` | `site:write` — drafting a blog post, triaging a form submission (session 46) |
 * | `publishesSite` | `site:publish` — publishing a post, configuring a form or a site hook (session 46) |
 */

import { ROLE_PERMISSIONS, type ClientPermission, type ClientRole } from '@fareast/shared'

import type { Principal } from '../../../auth/principal.js'

/** The role's name and what the *Approve* column says about it. */
export type RoleWords = { readonly name: string; readonly approves: string }

export const ROLE_WORDS: Readonly<Record<ClientRole, RoleWords>> = {
  owner: { name: 'Owner', approves: 'Yes' },
  admin: { name: 'Admin', approves: 'Yes' },
  billing: { name: 'Billing', approves: 'Invoices' },
  member: { name: 'Member', approves: 'Comment' },
}

/** One choice in the *Role* select. */
export type RoleOption = { readonly value: ClientRole; readonly label: string }

const ALL_ROLES: readonly RoleOption[] = (Object.keys(ROLE_WORDS) as ClientRole[]).map((role) => ({
  value: role,
  label: ROLE_WORDS[role].name,
}))

/**
 * The roles this viewer may hand out. In the order the table lists them.
 *
 * `owner` is missing for anybody who is not one, because
 * `clients.service.assertMayChangeOwnership` refuses it — "Only an owner can appoint
 * or remove another owner" — and this page's own rule is that a button somebody
 * would be refused for is **absent**, not disabled and not offered.
 */
export function roleOptionsFor(appointsOwners: boolean): readonly RoleOption[] {
  return appointsOwners ? ALL_ROLES : ALL_ROLES.filter((option) => option.value !== 'owner')
}

/**
 * What this page lets the signed-in person do.
 *
 * Staff impersonating a client hold the client's own role (`auth/principal.tsx`),
 * so they land on the same row — and money is refused separately, before the
 * request is sent (`api/hooks.ts`).
 */
export type SettingsAbilities = {
  readonly seesPeople: boolean
  readonly managesSeats: boolean
  readonly editsInvoiceDetails: boolean
  readonly choosesRail: boolean
  readonly approvesMoney: boolean
  readonly appointsOwners: boolean
  /**
   * `requests:write` — raising a request, editing a draft, and answering a review
   * request on a deliverable.
   *
   * It is not a settings ability and it is here anyway, because this file is the one
   * reader of `ROLE_PERMISSIONS` on this side of the boundary: a second copy in
   * `pages/client/requests` or `pages/client/files` is the hand-written matrix
   * session 19 deleted, growing back. `member` holds it and `billing` does not.
   *
   * It is also what the **nav** asks (`shell/nav.ts` `needs`), and that is not a
   * coincidence dressed up as a rule. `.docs/19` §Edge cases refuses a `billing`
   * seat exactly three pages — requests, projects and files — and `requests:write`
   * is the only permission in the matrix whose column is that set: `invoices:read`
   * would take the pages away from a `member` too, and `money:approve` would take
   * them from a `member` and give them to nobody a `billing` seat is not already.
   */
  readonly raisesRequests: boolean
  /**
   * `site:write` and `site:publish` (session 46) — the blog and the forms, the first
   * things a client publishes. Here for `raisesRequests`' reason: this file is the one
   * reader of `ROLE_PERMISSIONS` on this side of the boundary, and the two workspaces
   * would otherwise grow a copy each. A member writes and does not publish.
   */
  readonly writesSite: boolean
  readonly publishesSite: boolean
}

/** `roleCan`, on this side of the boundary. The same table, read the same way. */
function can(role: ClientRole | null, permission: ClientPermission): boolean {
  return role !== null && ROLE_PERMISSIONS[role].includes(permission)
}

export function abilitiesOf(principal: Principal): SettingsAbilities {
  const role = principal.kind === 'client_user' ? principal.role : null

  return {
    /*
     * `role !== null` is load-bearing rather than defensive. Nobody past
     * `guardClientPortal` has a null role, but the predicate has to say what its own
     * docstring says — "billing sees billing, settings (payment), invoices" is a
     * claim about the four client roles, and an anonymous principal is not one of
     * them being shown the People table.
     */
    seesPeople: role !== null && role !== 'billing',
    managesSeats: can(role, 'members:manage'),
    editsInvoiceDetails: can(role, 'members:manage'),
    choosesRail: can(role, 'invoices:pay'),
    approvesMoney: can(role, 'money:approve'),
    appointsOwners: role === 'owner',
    raisesRequests: can(role, 'requests:write'),
    writesSite: can(role, 'site:write'),
    publishesSite: can(role, 'site:publish'),
  }
}
